import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, authorizedFetch } from "@/lib/api";
import { API_URL, toIntOrNull, toStrOrNull, toUuidOrNull } from "@/lib/mutation-utils";

interface MutationOptions {
  onSuccess?: () => void;
  onCreateSuccess?: () => void;
  onUpdateSuccess?: () => void;
  onAvatarSuccess?: () => void;
  onError?: (error: Error) => void;
}

function buildPayload(data: any) {
  return {
    code:       data.code,
    name:       data.name,
    fileId:     toIntOrNull(data.fileId),
    sgtCodes:   Array.isArray(data.sgtCodes) && data.sgtCodes.length > 0 ? data.sgtCodes : null,
    docId:      toStrOrNull(data.docId),
    docTypeId:  toUuidOrNull(data.docTypeId),
    address:    toStrOrNull(data.address),
    cityName:   toStrOrNull(data.cityName),
    postalCode: toStrOrNull(data.postalCode),
    stateId:    toUuidOrNull(data.stateId),
    countryId:  toUuidOrNull(data.countryId),
  };
}

export function useClientMutations(options: MutationOptions = {}) {
  const qc = useQueryClient();

  const createMut = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.clients.$post({ json: buildPayload(data) });
      // `api` throws on non-2xx via withHandledFetch
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente creado exitosamente");
      options.onCreateSuccess?.();
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await api.clients[":id"].$patch({ param: { id }, json: buildPayload(data) });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente actualizado exitosamente");
      options.onUpdateSuccess?.();
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.clients[":id"].$delete({ param: { id } });
      // throws on !ok
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente eliminado exitosamente");
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(`${API_URL}/api/clients/${id}/restore`, {
        method: "PATCH",
      });
      // `authorizedFetch` throws on !ok
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente restaurado exitosamente");
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const updateAvatarMut = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      // Step 1: presign
      const presignRes = await authorizedFetch(`${API_URL}/api/clients/${id}/avatar/presign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileName: file.name, mimeType: file.type }),
      });
      const { uploadUrl, key } = await presignRes.json() as { uploadUrl: string; key: string };

      // Step 2: PUT directly to S3 (no auth headers)
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      // Step 3: confirm — returns { avatarKey, avatarUrl (signed) }
      await authorizedFetch(`${API_URL}/api/clients/${id}/avatar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key }),
      });
    },
    onSuccess: (_data, { id }) => {
      // Refetch list so avatarUrl (signed) is updated — but do NOT close the sheet.
      qc.invalidateQueries({ queryKey: ["clients"] });
      // Bust the per-client avatar cache used by ClientAvatar in shipments table/kanban.
      qc.invalidateQueries({ queryKey: ["client-avatar-url", id] });
      toast.success("Avatar actualizado");
      options.onAvatarSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const deleteAvatarMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/clients/${id}/avatar`, {
        method: "DELETE",
      });
    },
    onSuccess: (_data, id) => {
      // Refetch list — but do NOT close the sheet.
      qc.invalidateQueries({ queryKey: ["clients"] });
      // Bust the per-client avatar cache used by ClientAvatar in shipments table/kanban.
      qc.invalidateQueries({ queryKey: ["client-avatar-url", id] });
      toast.success("Avatar eliminado");
      options.onAvatarSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  return {
    createMut,
    updateMut,
    deleteMut,
    restoreMut,
    updateAvatarMut,
    deleteAvatarMut,
    isSaving: createMut.isPending || updateMut.isPending,
  };
}
